/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useCallback, MouseEvent } from 'react';
import {
  GDSIconSource,
  GDSStyleObject,
  GDSTextActionIndicator,
  GDSTextActionSize,
  TextAction,
} from '@leagueplatform/genesis-core';
import { InternalOrExternalLink as Link } from '@leagueplatform/web-common-components';
import {
  type MasonryDriverAction,
  useInvokeAction,
} from '@leagueplatform/masonry-renderers';

export type MasonryTextActionVariant = 'default' | 'destructive' | 'subtle';

export type MasonryTextActionProperties = {
  text?: string;
  icon?: GDSIconSource;
  indicator?: GDSTextActionIndicator;
  size?: GDSTextActionSize;
  onClick?: MasonryDriverAction;
  variant?: MasonryTextActionVariant;
  className?: string;
  loading?: boolean;
  css?: GDSStyleObject;
};

export const MasonryTextAction = ({
  icon,
  indicator,
  onClick,
  size,
  text,
  variant = 'default',
  className,
  loading,
  css,
  ...props
}: MasonryTextActionProperties) => {
  const invokeAction = useInvokeAction();
  const isTextActionLink =
    onClick?.type === 'deeplink' && Boolean(onClick?.payload?.url);

  const handleOnClick = useCallback(
    (event: MouseEvent) => {
      if (onClick) {
        invokeAction({
          event,
          ...onClick,
        });
      }
    },
    [invokeAction, onClick],
  );

  const textActionLinkUrl = useMemo(
    () => (isTextActionLink ? onClick?.payload?.url : undefined),
    [isTextActionLink, onClick],
  );

  const variantProps = useMemo(() => {
    switch (variant) {
      case 'default':
        return { emphasis: 'base' } as const;
      case 'subtle':
        return { emphasis: 'subtle' } as const;
      case 'destructive':
        return { destructive: true } as const;
      default:
        return undefined;
    }
  }, [variant]);

  return (
    <TextAction
      as={isTextActionLink ? Link : 'button'}
      {...(textActionLinkUrl && { href: textActionLinkUrl })}
      icon={icon}
      indicator={indicator}
      onClick={handleOnClick}
      size={size}
      className={className}
      loading={loading}
      css={css}
      {...variantProps}
      {...props}
    >
      {text}
    </TextAction>
  );
};

// Adding a display name here because some components use the display name to identify the component type and only allow TextAction in certain slots.
MasonryTextAction.displayName = 'MasonryTextAction';
